import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import { ManufacturingSyncStatus } from '../../types/manufacturing';

interface Props {
  status: ManufacturingSyncStatus | null;
  loading: boolean;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const ageMs = Date.now() - ts;
  const ageHours = ageMs / (1000 * 60 * 60);
  if (ageHours < 1) return `${Math.round(ageMs / 60000)}m ago`;
  if (ageHours < 48) return `${Math.round(ageHours)}h ago`;
  return `${Math.round(ageHours / 24)}d ago`;
}

// Tooltip text is bounded to keep any error string from the snapshot
// pipeline from rendering raw stack traces or echoed request bodies.
// Backend already sanitizes + trims to 200 chars; this is defense in
// depth.
function tooltipFor(error: string | null, startedAt: string): string {
  if (!error) return startedAt;
  return error.length > 200 ? `${error.slice(0, 200)}…` : error;
}

export function SyncStatusBanner({ status, loading }: Props) {
  if (loading && !status) {
    return (
      <Alert severity="info" sx={{ mb: 2 }} data-testid="manufacturing-sync-status">
        Loading snapshot status…
      </Alert>
    );
  }

  if (!status || status.entries.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} data-testid="manufacturing-sync-status">
        <AlertTitle>No snapshot has run yet</AlertTitle>
        Run <code>python run_manufacturing_snapshot.py</code> and{' '}
        <code>python run_onshape_snapshot.py</code> from the backend to populate manufacturing.* and
        onshape.* tables. Until then, every section below will be empty.
      </Alert>
    );
  }

  const failed = status.entries.filter((e) => e.status === 'failed');
  const severity: 'success' | 'warning' | 'error' =
    failed.length > 0 ? 'error' : status.latest_at ? 'success' : 'warning';

  return (
    <Alert severity={severity} sx={{ mb: 2 }} data-testid="manufacturing-sync-status">
      <AlertTitle>
        {status.latest_at
          ? `Latest snapshot ${formatRelative(status.latest_at)}`
          : 'Snapshot in progress'}
      </AlertTitle>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
        {status.entries.map((entry) => (
          <Chip
            key={`${entry.schema_name}.${entry.table_name}`}
            size="small"
            label={`${entry.schema_name}.${entry.table_name}: ${entry.rows_inserted}`}
            color={
              entry.status === 'success'
                ? 'success'
                : entry.status === 'failed'
                  ? 'error'
                  : 'default'
            }
            variant={entry.status === 'success' ? 'outlined' : 'filled'}
            title={tooltipFor(entry.error, entry.started_at)}
          />
        ))}
      </Box>
      {failed.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
          {failed.length} table(s) failed on the last run. Hover a chip to see the error.
        </Typography>
      )}
    </Alert>
  );
}
